import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { Copy, Mail, MessageCircle, Share2, Check } from 'lucide-react';

interface SharePublicModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Public share modal — promotes the KinsCircle platform itself, NOT a
// specific family invite. Always points at the public /join page so
// recipients can start their own family tree, with no auth required.
const PUBLIC_URL = 'https://www.kinscircleapp.com/join';
const PUBLIC_MESSAGE =
  "I'm building my family tree on KinsCircle — start yours.";

const WhatsAppIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l.6.952-1 3.648 3.379-.999zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
  </svg>
);

const SharePublicModal: React.FC<SharePublicModalProps> = ({ open, onOpenChange }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${PUBLIC_MESSAGE} ${PUBLIC_URL}`);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — please copy the link manually');
    }
  };

  const handleWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(`${PUBLIC_MESSAGE} ${PUBLIC_URL}`)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSMS = () => {
    const body = encodeURIComponent(`${PUBLIC_MESSAGE} ${PUBLIC_URL}`);
    // sms: scheme works on iOS & Android — desktop will gracefully fall through.
    window.location.href = `sms:?&body=${body}`;
  };

  const handleEmail = () => {
    const subject = encodeURIComponent('Join me on KinsCircle');
    const body = encodeURIComponent(`${PUBLIC_MESSAGE}\n\n${PUBLIC_URL}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'KinsCircle',
          text: PUBLIC_MESSAGE,
          url: PUBLIC_URL,
        });
      } catch {
        /* user cancelled — silent */
      }
    } else {
      handleCopy();
    }
  };

  const canNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Share2 className="w-5 h-5 text-fuchsia-600" />
            Share KinsCircle
          </DialogTitle>
          <DialogDescription>
            Help others build their family's living legacy.
          </DialogDescription>
        </DialogHeader>

        {/* Pre-filled message preview */}
        <div className="rounded-xl border border-fuchsia-100 bg-fuchsia-50/60 p-3 text-sm text-slate-700">
          <p className="italic leading-relaxed">"{PUBLIC_MESSAGE}"</p>
          <p className="mt-2 text-fuchsia-700 font-medium break-all">{PUBLIC_URL}</p>
        </div>

        <div className="grid grid-cols-2 gap-2 pt-1">
          <Button
            onClick={handleCopy}
            variant="outline"
            className="rounded-xl h-12 justify-start gap-2 border-slate-200"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-emerald-600" />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                <span>Copy Link</span>
              </>
            )}
          </Button>

          <Button
            onClick={handleWhatsApp}
            className="rounded-xl h-12 justify-start gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white"
          >
            <WhatsAppIcon className="w-4 h-4" />
            <span>WhatsApp</span>
          </Button>

          <Button
            onClick={handleSMS}
            variant="outline"
            className="rounded-xl h-12 justify-start gap-2 border-slate-200"
          >
            <MessageCircle className="w-4 h-4" />
            <span>SMS</span>
          </Button>

          <Button
            onClick={handleEmail}
            variant="outline"
            className="rounded-xl h-12 justify-start gap-2 border-slate-200"
          >
            <Mail className="w-4 h-4" />
            <span>Email</span>
          </Button>
        </div>

        {canNativeShare && (
          <Button
            onClick={handleNativeShare}
            className="w-full rounded-xl bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:opacity-95 text-white gap-2"
          >
            <Share2 className="w-4 h-4" />
            More sharing options
          </Button>
        )}

        <p className="text-[11px] text-slate-500 text-center pt-1">
          Anyone you share with can join publicly — no invite link required.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default SharePublicModal;
